import type {
  ApplicablePolicyHit,
  EffectiveRestriction,
  GraphAsset,
  GraphCatalog,
  GraphColumn,
  GraphContext,
  GraphLevel,
  GraphModel,
  GraphPolicy,
  GraphUserContext,
  GraphVault,
  OwnershipLevel,
  PolicyCoverage,
  PolicyOverlap,
} from './policyGraphTypes';

function unique<T>(values: T[]): T[] {
  return Array.from(new Set(values));
}

export function vaultById(catalog: GraphCatalog, vaultId: string): GraphVault | undefined {
  return catalog.vaults.find((vault) => vault.id === vaultId);
}

export function modelById(catalog: GraphCatalog, modelId: string): GraphModel | undefined {
  return catalog.models.find((model) => model.id === modelId);
}

export function assetById(catalog: GraphCatalog, assetId: string): GraphAsset | undefined {
  return catalog.assets.find((asset) => asset.id === assetId);
}

export function policyById(catalog: GraphCatalog, policyId: string): GraphPolicy | undefined {
  return catalog.policies.find((policy) => policy.id === policyId);
}

export function ownerName(catalog: GraphCatalog, policy: GraphPolicy): string {
  if (policy.ownership.level === 'ASSET' && policy.ownership.assetId) {
    return assetById(catalog, policy.ownership.assetId)?.name ?? 'Asset';
  }
  if (policy.ownership.level === 'MODEL' && policy.ownership.modelId) {
    return modelById(catalog, policy.ownership.modelId)?.name ?? 'Data Model';
  }
  return vaultById(catalog, policy.ownership.vaultId)?.name ?? 'DataVault';
}

export function modelsInVault(catalog: GraphCatalog, vaultId: string): GraphModel[] {
  return catalog.models.filter((model) => model.vaultId === vaultId);
}

export function assetsInVault(catalog: GraphCatalog, vaultId: string): GraphAsset[] {
  return catalog.assets.filter((asset) => asset.vaultId === vaultId);
}

export function assetsInModel(catalog: GraphCatalog, modelId: string): GraphAsset[] {
  const model = modelById(catalog, modelId);
  if (!model) return [];
  return catalog.assets.filter((asset) => model.assetIds.includes(asset.id));
}

export function scopedAssets(catalog: GraphCatalog, policy: GraphPolicy): GraphAsset[] {
  if (policy.ownership.level === 'ASSET' && policy.ownership.assetId) {
    const asset = assetById(catalog, policy.ownership.assetId);
    return asset ? [asset] : [];
  }
  if (policy.ownership.level === 'MODEL' && policy.ownership.modelId) {
    return assetsInModel(catalog, policy.ownership.modelId);
  }
  return assetsInVault(catalog, policy.ownership.vaultId);
}

export function inheritedModels(catalog: GraphCatalog, policy: GraphPolicy): GraphModel[] {
  if (policy.ownership.level === 'VAULT') return modelsInVault(catalog, policy.ownership.vaultId);
  return [];
}

export function inheritedAssets(catalog: GraphCatalog, policy: GraphPolicy): GraphAsset[] {
  if (policy.ownership.level === 'ASSET') return [];
  return scopedAssets(catalog, policy);
}

function columnMatches(policy: GraphPolicy, column: GraphColumn): boolean {
  const names = policy.match.columnNames;
  const classes = policy.match.classifications;
  const nameHit = Boolean(names?.length && names.includes(column.name));
  const classHit = Boolean(classes?.length && classes.includes(column.classification));
  if (names?.length && classes?.length) return nameHit || classHit;
  if (names?.length) return nameHit;
  if (classes?.length) return classHit;
  return false;
}

export function matchingColumns(policy: GraphPolicy, asset: GraphAsset): GraphColumn[] {
  if (policy.match.assetIds?.length && !policy.match.assetIds.includes(asset.id)) return [];
  if (policy.action.kind === 'filter') {
    if (policy.match.requireRegion && !asset.columns.some((column) => column.name === 'region')) return [];
    if (policy.match.requireConsent && !asset.columns.some((column) => column.name === 'consent')) return [];
    return [];
  }
  if (policy.action.kind === 'deny' && policy.action.denyTarget === 'asset') {
    if (policy.match.assetIds?.length) return policy.match.assetIds.includes(asset.id) ? asset.columns : [];
    return asset.columns;
  }
  if (policy.action.kind === 'allow') return [];
  return asset.columns.filter((column) => columnMatches(policy, column));
}

export function assetMatchesPolicy(policy: GraphPolicy, asset: GraphAsset): boolean {
  if (policy.match.assetIds?.length && !policy.match.assetIds.includes(asset.id)) return false;
  if (policy.action.kind === 'filter') {
    if (policy.match.requireRegion) return asset.columns.some((column) => column.name === 'region');
    if (policy.match.requireConsent) return asset.columns.some((column) => column.name === 'consent');
    return true;
  }
  if (policy.action.kind === 'deny' && policy.action.denyTarget === 'asset') {
    return !policy.match.assetIds?.length || policy.match.assetIds.includes(asset.id);
  }
  if (policy.match.classifications?.length || policy.match.columnNames?.length) {
    return matchingColumns(policy, asset).length > 0;
  }
  return true;
}

export function matchedAssets(catalog: GraphCatalog, policy: GraphPolicy): GraphAsset[] {
  return scopedAssets(catalog, policy).filter((asset) => assetMatchesPolicy(policy, asset));
}

export function policyCoverage(catalog: GraphCatalog, policy: GraphPolicy): PolicyCoverage {
  const scoped = scopedAssets(catalog, policy);
  const matched = scoped.filter((asset) => assetMatchesPolicy(policy, asset));
  const protectedColumns = matched.flatMap((asset) =>
    matchingColumns(policy, asset).map((column) => ({ assetId: asset.id, column: column.name })),
  );
  return {
    policyId: policy.id,
    assetsEvaluated: scoped.length,
    assetsMatched: matched.length,
    columnsProtected: unique(protectedColumns.map((item) => `${item.assetId}.${item.column}`)).length,
    matchedAssetIds: matched.map((asset) => asset.id),
    scopedAssetIds: scoped.map((asset) => asset.id),
    protectedColumns,
  };
}

export function featuredThenRest(assets: GraphAsset[]): GraphAsset[] {
  return [...assets].sort((left, right) => Number(right.featured) - Number(left.featured) || left.name.localeCompare(right.name));
}

export function policyAppliesToUser(policy: GraphPolicy, user: GraphUserContext): boolean {
  if (policy.audience.exemptRoles?.some((role) => user.roles.includes(role))) return false;
  if (policy.audience.mode === 'roles') {
    return Boolean(policy.audience.roles?.some((role) => user.roles.includes(role)));
  }
  return true;
}

export function policiesOwnedAt(catalog: GraphCatalog, context: GraphContext): GraphPolicy[] {
  return catalog.policies.filter((policy) => {
    if (context.level === 'vault') return policy.ownership.level === 'VAULT' && policy.ownership.vaultId === context.vaultId;
    if (context.level === 'model') return policy.ownership.level === 'MODEL' && policy.ownership.modelId === context.modelId;
    return policy.ownership.level === 'ASSET' && policy.ownership.assetId === context.assetId;
  });
}

export function inheritedPoliciesFor(catalog: GraphCatalog, context: GraphContext): GraphPolicy[] {
  if (context.level === 'vault') return [];
  const vaultOwned = catalog.policies.filter((policy) => policy.ownership.level === 'VAULT' && policy.ownership.vaultId === context.vaultId);
  if (context.level === 'model') return vaultOwned;
  const modelOwned = catalog.policies.filter((policy) => {
    if (policy.ownership.level !== 'MODEL' || !context.assetId) return false;
    const asset = assetById(catalog, context.assetId);
    return Boolean(asset && policy.ownership.modelId && asset.modelIds.includes(policy.ownership.modelId));
  });
  return [...vaultOwned, ...modelOwned];
}

export function applicablePolicies(catalog: GraphCatalog, context: GraphContext): GraphPolicy[] {
  if (context.level !== 'asset' || !context.assetId) {
    return uniqueIds([...inheritedPoliciesFor(catalog, context), ...policiesOwnedAt(catalog, context)]);
  }
  const asset = assetById(catalog, context.assetId);
  if (!asset) return [];
  return catalog.policies.filter((policy) => {
    if (!scopedAssets(catalog, policy).some((item) => item.id === asset.id)) return false;
    if (policy.ownership.assetId === asset.id) return true;
    return assetMatchesPolicy(policy, asset);
  });
}

export function overviewPolicies(catalog: GraphCatalog, context: GraphContext): GraphPolicy[] {
  if (context.level === 'vault') return policiesOwnedAt(catalog, context);
  if (context.level === 'model') {
    return uniqueIds([...inheritedPoliciesFor(catalog, context), ...policiesOwnedAt(catalog, context)]);
  }
  return applicablePolicies(catalog, context);
}

function uniqueIds(policies: GraphPolicy[]): GraphPolicy[] {
  const byId = new Map<string, GraphPolicy>();
  policies.forEach((policy) => byId.set(policy.id, policy));
  return Array.from(byId.values());
}

export function resourceKey(assetId: string, column?: string): string {
  return column ? `${assetId}.${column}` : assetId;
}

function actionRank(policy: GraphPolicy): number {
  if (policy.action.kind === 'deny') return 4;
  if (policy.action.kind === 'mask') return 2;
  if (policy.action.kind === 'filter') return 1;
  return 0;
}

function overlapKindFor(left: GraphPolicy, right: GraphPolicy): { kind: PolicyOverlap['kind']; attention: boolean; effectiveLabel: string; explanation: string } {
  const pair = [left, right];
  const deny = pair.find((policy) => policy.action.kind === 'deny');
  const masks = pair.filter((policy) => policy.action.kind === 'mask');
  const filters = pair.filter((policy) => policy.action.kind === 'filter');
  const allow = pair.find((policy) => policy.action.kind === 'allow');
  const mandatoryParent = pair.find((policy) => policy.mandatory && (policy.ownership.level === 'VAULT' || policy.ownership.level === 'MODEL'));
  const weakerChild = pair.find((policy) => !policy.mandatory && actionRank(policy) < actionRank(mandatoryParent ?? policy));

  if (deny && masks.length) {
    return {
      kind: 'compatible',
      attention: true,
      effectiveLabel: 'Deny Access',
      explanation: `${deny.name} denies access. ${masks.map((policy) => policy.name).join(' and ')} still apply but cannot override the denial.`,
    };
  }
  if (deny && allow) {
    return {
      kind: 'conflict',
      attention: true,
      effectiveLabel: 'Deny Access',
      explanation: `Deny takes precedence over Allow. ${deny.name} wins over ${allow.name}.`,
    };
  }
  if (mandatoryParent && weakerChild && actionRank(weakerChild) < actionRank(mandatoryParent)) {
    return {
      kind: 'conflict',
      attention: true,
      effectiveLabel: mandatoryParent.action.label,
      explanation: `A parent-level mandatory restriction from ${mandatoryParent.name} cannot be weakened by ${weakerChild.name}.`,
    };
  }
  if (masks.length === 2 && masks[0].action.mask && masks[1].action.mask && masks[0].action.mask !== masks[1].action.mask) {
    return {
      kind: 'unresolved',
      attention: true,
      effectiveLabel: 'Unresolved mask conflict',
      explanation: `${masks[0].name} uses ${masks[0].action.label} while ${masks[1].name} uses ${masks[1].action.label}. Incompatible masking rules are not ranked automatically.`,
    };
  }
  if (filters.length === 2) {
    return {
      kind: 'compatible',
      attention: false,
      effectiveLabel: 'Combined row filters (AND)',
      explanation: `${filters[0].name} and ${filters[1].name} both filter rows. Applicable filters combine restrictively using AND.`,
    };
  }
  if (masks.length === 2) {
    return {
      kind: 'compatible',
      attention: false,
      effectiveLabel: masks[0].action.label,
      explanation: `${masks[0].name} and ${masks[1].name} apply the same masking action.`,
    };
  }
  return {
    kind: 'compatible',
    attention: false,
    effectiveLabel: deny?.action.label ?? left.action.label,
    explanation: `${left.name} and ${right.name} both apply to this resource. The stronger restriction is kept.`,
  };
}

function overlapSeverity(item: { kind: PolicyOverlap['kind']; attention: boolean }): number {
  if (item.kind === 'unresolved') return 3;
  if (item.kind === 'conflict') return 2;
  if (item.attention) return 1;
  return 0;
}

export function identifyOverlaps(catalog: GraphCatalog): PolicyOverlap[] {
  const byResource = new Map<string, { assetId: string; column?: string; label: string; policies: GraphPolicy[] }>();
  const add = (key: string, assetId: string, label: string, policy: GraphPolicy, column?: string) => {
    const current = byResource.get(key) ?? { assetId, column, label, policies: [] };
    if (!current.policies.some((item) => item.id === policy.id)) current.policies.push(policy);
    byResource.set(key, current);
  };
  catalog.policies.forEach((policy) => {
    matchedAssets(catalog, policy).forEach((asset) => {
      add(resourceKey(asset.id), asset.id, asset.name, policy);
      matchingColumns(policy, asset).forEach((column) => {
        add(resourceKey(asset.id, column.name), asset.id, `${asset.name}.${column.name}`, policy, column.name);
      });
    });
  });

  const byPair = new Map<string, PolicyOverlap>();
  byResource.forEach((entry) => {
    if (entry.policies.length < 2) return;
    for (let i = 0; i < entry.policies.length; i += 1) {
      for (let j = i + 1; j < entry.policies.length; j += 1) {
        const left = entry.policies[i];
        const right = entry.policies[j];
        const resolved = overlapKindFor(left, right);
        const pairId = [left.id, right.id].sort().join(':');
        const next: PolicyOverlap = {
          id: pairId,
          policyIds: [left.id, right.id],
          resource: { kind: entry.column ? 'column' : 'asset', assetId: entry.assetId, column: entry.column, label: entry.label },
          kind: resolved.kind,
          effectiveLabel: resolved.effectiveLabel,
          explanation: resolved.explanation,
          attention: resolved.attention,
        };
        const previous = byPair.get(pairId);
        if (!previous || overlapSeverity(next) > overlapSeverity(previous) || (overlapSeverity(next) === overlapSeverity(previous) && entry.column && !previous.resource.column)) {
          byPair.set(pairId, next);
        }
      }
    }
  });
  return Array.from(byPair.values());
}

export function overlapsForPolicy(overlaps: PolicyOverlap[], policyId: string): PolicyOverlap[] {
  return overlaps.filter((item) => item.policyIds.includes(policyId));
}

export function overlapsForResource(overlaps: PolicyOverlap[], assetId: string, column?: string): PolicyOverlap[] {
  return overlaps.filter((item) => {
    if (item.resource.assetId !== assetId) return false;
    if (column) return item.resource.column === column || !item.resource.column;
    return true;
  });
}

function originOf(policy: GraphPolicy): OwnershipLevel {
  return policy.ownership.level;
}

export function hitsForAsset(catalog: GraphCatalog, asset: GraphAsset, user: GraphUserContext): ApplicablePolicyHit[] {
  return catalog.policies
    .filter((policy) => policy.status === 'ACTIVE')
    .filter((policy) => scopedAssets(catalog, policy).some((item) => item.id === asset.id))
    .filter((policy) => assetMatchesPolicy(policy, asset))
    .map((policy) => ({
      policy,
      origin: originOf(policy),
      actionLabel: policy.action.label,
      appliesToUser: policyAppliesToUser(policy, user),
    }));
}

export function hitsForColumn(catalog: GraphCatalog, asset: GraphAsset, column: GraphColumn, user: GraphUserContext): ApplicablePolicyHit[] {
  return catalog.policies
    .filter((policy) => policy.status === 'ACTIVE')
    .filter((policy) => scopedAssets(catalog, policy).some((item) => item.id === asset.id))
    .filter((policy) => {
      if (!assetMatchesPolicy(policy, asset)) return false;
      if (policy.action.kind === 'filter') return false;
      if (policy.action.kind === 'deny' && policy.action.denyTarget === 'asset') return true;
      return matchingColumns(policy, asset).some((item) => item.name === column.name);
    })
    .map((policy) => ({
      policy,
      origin: originOf(policy),
      actionLabel: policy.action.label,
      appliesToUser: policyAppliesToUser(policy, user),
    }));
}

function explainColumn(asset: GraphAsset, column: GraphColumn, hits: ApplicablePolicyHit[]): EffectiveRestriction {
  const applicable = hits.filter((hit) => hit.appliesToUser);
  const deny = applicable.find((hit) => hit.policy.action.kind === 'deny');
  const masks = applicable.filter((hit) => hit.policy.action.kind === 'mask');
  const maskKinds = unique(masks.map((hit) => hit.policy.action.mask).filter(Boolean));
  const unresolved: string[] = [];

  if (deny) {
    const maskNote = masks.length
      ? ` The inherited masking policies remain applicable but do not override the denial.`
      : '';
    return {
      kind: 'deny',
      label: 'Deny Access',
      explanation: `The ${column.name} column is denied because the ${OWNER_WORD[deny.origin]}-level restriction denies access.${maskNote}`,
      attention: masks.length > 0,
      hits,
      filters: [],
      unresolved,
      simulation: true,
    };
  }

  const mandatoryParent = applicable.find((hit) => hit.policy.mandatory && hit.policy.ownership.level !== 'ASSET');
  const weaker = applicable.find((hit) => mandatoryParent && actionRank(hit.policy) < actionRank(mandatoryParent.policy));
  if (mandatoryParent && weaker) {
    return {
      kind: mandatoryParent.policy.action.kind === 'mask' ? 'mask' : 'deny',
      label: mandatoryParent.actionLabel,
      explanation: `A parent-level mandatory restriction from ${mandatoryParent.policy.name} cannot be weakened by ${weaker.policy.name}.`,
      attention: true,
      hits,
      filters: [],
      unresolved,
      simulation: true,
    };
  }

  if (maskKinds.length > 1) {
    unresolved.push(`${asset.name}.${column.name} has incompatible masking rules`);
    return {
      kind: 'unresolved',
      label: 'Unresolved mask conflict',
      explanation: `Multiple masking rules apply to ${asset.name}.${column.name} and they are not compatible. The graph does not pick a winner.`,
      attention: true,
      hits,
      filters: [],
      unresolved,
      simulation: true,
    };
  }

  if (masks.length) {
    return {
      kind: 'mask',
      label: masks[0].actionLabel,
      explanation: `${column.name} is protected by ${masks.map((hit) => hit.policy.name).join(' and ')}.`,
      attention: false,
      hits,
      filters: [],
      unresolved,
      simulation: true,
    };
  }

  return {
    kind: 'allow',
    label: 'No column restriction',
    explanation: `No active masking or denial policy matches ${asset.name}.${column.name} for this user context.`,
    attention: false,
    hits,
    filters: [],
    unresolved,
    simulation: true,
  };
}

const OWNER_WORD: Record<OwnershipLevel, string> = {
  VAULT: 'DataVault',
  MODEL: 'Data Model',
  ASSET: 'Asset',
};

export function effectiveForColumn(
  catalog: GraphCatalog,
  asset: GraphAsset,
  column: GraphColumn,
  user: GraphUserContext,
): EffectiveRestriction {
  return explainColumn(asset, column, hitsForColumn(catalog, asset, column, user));
}

export function effectiveForAsset(catalog: GraphCatalog, asset: GraphAsset, user: GraphUserContext): EffectiveRestriction {
  const hits = hitsForAsset(catalog, asset, user);
  const applicable = hits.filter((hit) => hit.appliesToUser);
  const deny = applicable.find((hit) => hit.policy.action.kind === 'deny');
  const filters = applicable.filter((hit) => hit.policy.action.kind === 'filter').map((hit) => hit.policy.action.filter ?? hit.actionLabel);
  if (deny) {
    return {
      kind: 'deny',
      label: 'Deny Access',
      explanation: `${asset.name} is denied by ${deny.policy.name} (${OWNER_WORD[deny.origin]}). Other applicable policies remain listed but do not override the denial.`,
      attention: applicable.length > 1,
      hits,
      filters,
      unresolved: [],
      simulation: true,
    };
  }
  if (filters.length) {
    return {
      kind: 'filter',
      label: 'Combined row filters (AND)',
      explanation: `Rows in ${asset.name} are limited by ${filters.join(' AND ')}.`,
      attention: false,
      hits,
      filters,
      unresolved: [],
      simulation: true,
    };
  }
  const masks = applicable.filter((hit) => hit.policy.action.kind === 'mask');
  if (masks.length) {
    return {
      kind: 'mask',
      label: 'Column protections apply',
      explanation: `${masks.length} masking ${masks.length === 1 ? 'policy' : 'policies'} protect columns on ${asset.name}.`,
      attention: false,
      hits,
      filters,
      unresolved: [],
      simulation: true,
    };
  }
  return {
    kind: 'allow',
    label: 'No additional restriction',
    explanation: `No active restriction matches ${asset.name} for this user context.`,
    attention: false,
    hits,
    filters,
    unresolved: [],
    simulation: true,
  };
}

export function resolveGraphContext(
  catalog: GraphCatalog,
  input: { level: GraphLevel; spaceId: string; modelId?: string; tableId?: string },
): GraphContext {
  const vaultId = catalog.vaults.some((vault) => vault.id === input.spaceId)
    ? input.spaceId
    : catalog.vaults[0]?.id ?? '';

  if (input.level === 'vault' || !vaultId) return { level: 'vault', vaultId };

  const modelId = input.modelId && catalog.models.some((model) => model.id === input.modelId)
    ? input.modelId
    : modelsInVault(catalog, vaultId)[0]?.id;

  if (input.level === 'model') return { level: 'model', vaultId, modelId };

  const assetId = input.tableId && catalog.assets.some((asset) => asset.id === input.tableId)
    ? input.tableId
    : assetsInVault(catalog, vaultId)[0]?.id;

  return { level: 'asset', vaultId, modelId, assetId };
}

export function filterPolicies(
  catalog: GraphCatalog,
  policies: GraphPolicy[],
  input: { query: string; type: 'all' | GraphPolicy['type']; ownership: 'all' | OwnershipLevel; status: 'all' | GraphPolicy['status'] },
): GraphPolicy[] {
  const query = input.query.trim().toLowerCase();
  return policies.filter((policy) => {
    if (input.type !== 'all' && policy.type !== input.type) return false;
    if (input.ownership !== 'all' && policy.ownership.level !== input.ownership) return false;
    if (input.status !== 'all' && policy.status !== input.status) return false;
    if (!query) return true;
    const coverage = policyCoverage(catalog, policy);
    const assetNames = coverage.matchedAssetIds.map((id) => assetById(catalog, id)?.name ?? '');
    const haystack = [policy.name, policy.description, policy.type, ownerName(catalog, policy), ...assetNames, ...coverage.protectedColumns.map((item) => item.column)].join(' ').toLowerCase();
    return haystack.includes(query);
  });
}
